import React, { memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import EmptySpecificationsPng from 'images/v5/empty-states/specifications.png';
import PlusSvg from 'images/v5/icons/plus.svg';
import Button from 'layout/atoms/Button/Button';
import InfiniteScroll from 'layout/atoms/InfiniteScroll';
import Loader from 'layout/atoms/Loader/Loader';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import SpecificationCard from 'layout/molecules/SpecificationCard';
import Grid from 'molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { addSpecification } from 'store/specificationDetailsSlice';
import { selectRootFolder } from 'store/specificationsFoldersSlice';
import { fetchSpecifications, resetSpecifications, selectSpecificationsStatus, setFilter, } from 'store/specificationsSlice';
function AllSpecifications({ orderSelect = false, warehouseReleaseSelect = false, latest = false, drafts = false, allFiles = false, }) {
    const dispatch = useDispatch();
    const layout = useSelector((state) => state.layout.specificationsDashboard);
    const { t } = useTranslation(['specification', 'exceptions', 'emptyStates']);
    const rootFolder = useSelector(selectRootFolder);
    const specifications = useSelector((state) => state.specifications.items);
    const filters = useSelector((state) => state.specifications.filters);
    const pageInfo = useSelector((state) => state.specifications.pageInfo);
    const fetchStatus = useSelector(selectSpecificationsStatus).fetch;
    const refetch = useSelector((state) => state.specifications.refetchFlags.fetch);
    const hasActiveFilters = filters.search !== '' || filters.statusFilter.length > 0 || filters.categoryFilter.length > 0;
    const productionSpecifications = specifications.filter((spec) => spec.state === 'PRODUCTION');
    const draftSpecifications = specifications.filter((spec) => spec.state === 'DRAFT');
    const displayedSpecifications = orderSelect || warehouseReleaseSelect ? productionSpecifications : specifications;
    let stateFilter = null;
    if (drafts) {
        stateFilter = 'DRAFT';
    }
    else if (orderSelect || warehouseReleaseSelect) {
        stateFilter = 'PRODUCTION';
    }
    const clearAndFetchSpecifications = useCallback(() => {
        dispatch(resetSpecifications());
        dispatch(fetchSpecifications({ states: stateFilter ? [stateFilter] : null, allFiles }));
    }, [dispatch, stateFilter, allFiles]);
    useEffect(() => {
        if (refetch) {
            clearAndFetchSpecifications();
        }
    }, [refetch, clearAndFetchSpecifications]);
    const fetchMoreSpecifications = () => {
        if (pageInfo.hasNextPage && fetchStatus !== 'loading') {
            dispatch(fetchSpecifications({ states: stateFilter ? [stateFilter] : null, allFiles }));
        }
    };
    const handleAddSpecification = () => {
        dispatch(addSpecification({ folderId: rootFolder === null || rootFolder === void 0 ? void 0 : rootFolder.rootFolderId })).then((response) => {
            var _a, _b;
            return ((_a = response.payload) === null || _a === void 0 ? void 0 : _a.id) &&
                dispatch(openModal({ name: 'specificationCreator', data: { id: (_b = response.payload) === null || _b === void 0 ? void 0 : _b.id } }));
        });
    };
    useEffect(() => {
        if (latest) {
            dispatch(setFilter({ filterName: 'orderBy', value: '-createdDate' }));
        }
    }, [dispatch, latest, displayedSpecifications]);
    useEffect(() => {
        if (filters) {
            clearAndFetchSpecifications();
        }
    }, [dispatch, filters, clearAndFetchSpecifications]);
    if (fetchStatus === 'idle' || fetchStatus === 'loading') {
        return React.createElement(Loader, null);
    }
    if (fetchStatus === 'succeeded' &&
        displayedSpecifications.length === 0 &&
        !orderSelect &&
        !warehouseReleaseSelect &&
        !drafts &&
        !hasActiveFilters) {
        return (React.createElement(EmptyState, { icon: React.createElement("img", { src: EmptySpecificationsPng, alt: "" }), header: t('emptyStates:SPECIFICATIONS_HEADER'), subheader: t('emptyStates:SPECIFICATIONS_SUBHEADER') },
            React.createElement(Button, { leftIcon: React.createElement(PlusSvg, null), onClick: handleAddSpecification }, t('emptyStates:SPECIFICATIONS_ADD_BUTTON'))));
    }
    if (fetchStatus === 'succeeded' && displayedSpecifications.length === 0 && hasActiveFilters) {
        return React.createElement(EmptyState, null);
    }
    if (drafts) {
        return (React.createElement(InfiniteScroll, { dataLength: draftSpecifications.length, next: fetchMoreSpecifications, hasMore: pageInfo.hasNextPage, loader: React.createElement(Loader, null), scrollableTarget: "mainContent" },
            React.createElement(Grid, { columns: layout === 'grid' ? 5 : 1, gap: 12 },
                draftSpecifications.length === 0 && (React.createElement("p", null, t('emptyStates:no_filters_found_for_products'))),
                draftSpecifications.map((specification) => {
                    return (React.createElement(SpecificationCard, { key: specification.id, specification: specification, layout: layout, orderSelect: orderSelect, warehouseReleaseSelect: warehouseReleaseSelect }));
                }))));
    }
    return (React.createElement(InfiniteScroll, { dataLength: displayedSpecifications.length, next: fetchMoreSpecifications, hasMore: pageInfo.hasNextPage, loader: React.createElement(Loader, null), scrollableTarget: "mainContent" },
        React.createElement(Grid, { columns: layout === 'grid' ? 'flexible' : 1, gap: 12 },
            displayedSpecifications.length === 0 && (React.createElement("p", null, t('emptyStates:no_filters_found_for_products'))),
            displayedSpecifications.map((specification) => {
                return (React.createElement(SpecificationCard, { key: specification.id, specification: specification, layout: layout, orderSelect: orderSelect, warehouseReleaseSelect: warehouseReleaseSelect }));
            }))));
}
export default memo(AllSpecifications);
